import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Chip,
  Typography,
  Collapse,
  Button,
  Stack,
  Skeleton,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocalShipping as DeliveryIcon,
  Restaurant as DineInIcon,
  ShoppingBag as PickupIcon,
  Visibility as ViewIcon,
  AccessTime as TimeIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { useStoreManagement, Store } from '../../context/StoreManagementContext';
import { useTimeFormat } from '../../context/TimeFormatContext';
import { format } from 'date-fns';
import { formatOccasionName } from '../StoreHours/types';

interface StoreListViewProps {
  stores: Store[];
  onStoreClick?: (store: Store) => void;
  isLoading?: boolean;
  viewMode?: 'list' | 'grid';
}

/**
 * Format a timestamp as relative time (e.g., "7 minutes ago")
 * Uses common thresholds for readability
 */
function formatRelativeTime(timestamp: string): string {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'Just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    // Show date for older items
    return date.toLocaleDateString('en-GB', {
      month: 'short',
      day: 'numeric',
      ...(date.getFullYear() !== now.getFullYear() && { year: 'numeric' }),
    });
  }
}

/**
 * Format a timestamp as full date/time for tooltip
 */
function formatFullTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleString('en-GB', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export const StoreListView: React.FC<StoreListViewProps> = ({
  stores,
  onStoreClick,
  isLoading = false,
  viewMode = 'list',
}) => {
  const {
    selectedStoreIds,
    toggleStoreSelection,
    selectAllStores,
    clearSelection,
  } = useStoreManagement();

  const { use24HourFormat } = useTimeFormat();

  // Compute selection state based on current stores
  const isAllSelected = useMemo(() => {
    return stores.length > 0 && stores.every(store => selectedStoreIds.has(store.storeNumber));
  }, [stores, selectedStoreIds]);

  const isPartiallySelected = useMemo(() => {
    const hasSelection = selectedStoreIds.size > 0;
    return hasSelection && !isAllSelected;
  }, [selectedStoreIds, isAllSelected]);

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const handleToggleExpand = (storeId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(storeId)) {
        newSet.delete(storeId);
      } else {
        newSet.add(storeId);
      }
      return newSet;
    });
  };

  const getStatusIcon = (store: Store) => {
    switch (store.status) {
      case 'active':
        return <ActiveIcon color="success" fontSize="small" />;
      case 'error':
        return <ErrorIcon color="error" fontSize="small" />;
      case 'inactive':
        return <WarningIcon color="warning" fontSize="small" />;
      default:
        return null;
    }
  };

  const getChannelChips = (store: Store) => {
    const chips: JSX.Element[] = [];

    // First add Web/App chip if onlineOrders data exists
    if (store.onlineOrders !== undefined) {
      chips.push(
        <Chip
          key="web-app"
          label="Web/App"
          size="small"
          color={store.onlineOrders ? 'success' : 'default'}
          variant={store.onlineOrders ? 'filled' : 'outlined'}
          sx={{
            fontSize: '0.7rem',
            opacity: store.onlineOrders ? 1 : 0.7
          }}
        />
      );
    }

    // Then add delivery channel chips
    const channels = store.channels || store.activeChannels || [];
    channels.forEach(ch => {
      // Determine if channel is active
      const isActive = ch.status === 'ONLINE' || ch.status === 'ACTIVE';
      const isPaused = ch.status === 'PAUSED';

      // Format the channel name
      let label = ch.channel;
      if (ch.channel === 'UBEREATS') {
        label = 'Uber Eats';
      } else if (ch.channel === 'DOORDASH') {
        label = 'DoorDash';
      } else if (ch.channel === 'GRUBHUB') {
        label = 'Grubhub';
      } else if (ch.channel === 'DELIVEROO') {
        label = 'Deliveroo';
      } else if (ch.channel === 'JUST_EAT') {
        label = 'Just Eat';
      }

      // Add delivery provider indicator if internal
      if (ch.deliveryProvider === 'INTERNAL') {
        label += ' (In-house)';
      }

      // Override color based on status
      let chipColor: 'success' | 'error' | 'warning' | 'default' = 'default';
      if (isActive) {
        chipColor = 'success'; // Green for active/online
      } else if (isPaused) {
        chipColor = 'warning'; // Yellow/orange for paused (temporary hold)
      } else {
        chipColor = 'default'; // Gray for deactivated (toggled off)
      }

      chips.push(
        <Chip
          key={`${ch.channel}-${ch.deliveryProvider}`}
          label={label}
          size="small"
          color={chipColor}
          variant={isActive ? 'filled' : 'outlined'}
          sx={{
            fontSize: '0.7rem',
            opacity: isPaused ? 0.8 : 1
          }}
        />
      );
    });

    return chips;
  };

  const formatTodayHours = (store: Store) => {
    // Get today's day of week and convert to ISO 8601 format
    // JavaScript: 0 = Sunday, 1 = Monday, ... 6 = Saturday
    // ISO 8601:   1 = Monday, ... 6 = Saturday, 7 = Sunday
    const jsDay = new Date().getDay();
    const today = jsDay === 0 ? 7 : jsDay; // Convert Sunday from 0 to 7

    // Helper to format time
    const formatTime = (hour: number, min: number) => {
      if (use24HourFormat) {
        return `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
      } else {
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
        return `${displayHour}:${String(min).padStart(2, '0')} ${period}`;
      }
    };

    // Helper to parse duration from HH:MM:SS or HH:MM format to minutes
    const parseDuration = (durationStr: string | number): number => {
      if (typeof durationStr === 'string' && durationStr.includes(':')) {
        const parts = durationStr.split(':').map(Number);
        if (parts.length >= 2) {
          return parts[0] * 60 + parts[1]; // hours * 60 + minutes
        }
      }
      return Number(durationStr) || 0;
    };

    // Helper to format a single time slot
    const formatTimeSlot = (hour: any): { startMinutes: number; formattedTime: string } | null => {
      const timeFrame = hour.timeFrame;
      if (!timeFrame) return null;

      // Skip special hours (those with date range)
      if (timeFrame.startDate && timeFrame.endDate) return null;

      const intervalStart = timeFrame.intervalStartTime || '00:00';
      const duration = parseDuration(timeFrame.duration || '0');

      const [startHour, startMin] = intervalStart.split(':').map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = startMinutes + duration;
      const endHour = Math.floor(endMinutes / 60) % 24;
      const endMin = endMinutes % 60;

      return {
        startMinutes,
        formattedTime: `${formatTime(startHour, startMin)} - ${formatTime(endHour, endMin)}`
      };
    };

    // Find occasions that are enabled (businessHours from Byte Graph)
    const occasions = store.hours as any[] || [];

    // Get enabled occasions and their hours (collecting ALL time slots for today)
    const enabledOccasions: { occasion: string; hours: string }[] = [];

    for (const occ of occasions) {
      if (occ.isEnabled && occ.hours && occ.hours.length > 0) {
        // Collect ALL hours entries for today (not just the first one - for split hours)
        const todayTimeSlots: { startMinutes: number; formattedTime: string }[] = [];

        for (const hour of occ.hours) {
          if (hour.timeFrame && hour.timeFrame.days && hour.timeFrame.days.includes(today)) {
            const slot = formatTimeSlot(hour);
            if (slot) {
              todayTimeSlots.push(slot);
            }
          }
        }

        if (todayTimeSlots.length > 0) {
          // Sort by start time to display in order
          todayTimeSlots.sort((a, b) => a.startMinutes - b.startMinutes);

          // Join multiple time slots with " & "
          const formattedTime = todayTimeSlots.map(s => s.formattedTime).join(' & ');

          enabledOccasions.push({
            occasion: formatOccasionName(occ.occasion),
            hours: formattedTime
          });
        }
      }
    }

    if (enabledOccasions.length === 0) {
      // If no occasions with hours found for today, check if any occasions are enabled
      const hasEnabledOccasions = occasions.some((occ: any) => occ.isEnabled);
      if (!hasEnabledOccasions) {
        return 'No hours available';
      }
      // Occasions exist but no hours for today
      return 'Closed today';
    }

    // If all occasions have the same hours, show once
    const uniqueHours = [...new Set(enabledOccasions.map(o => o.hours))];
    if (uniqueHours.length === 1 && uniqueHours[0]) {
      return uniqueHours[0];
    }

    // Find the earliest opening and latest closing across all occasions
    let earliestOpen = 24 * 60; // Start with end of day in minutes
    let latestClose = 0;

    enabledOccasions.forEach(occasion => {
      if (occasion.hours) {
        // Parse ALL time ranges in the hours string (e.g., "11:00 AM - 7:00 PM & 8:00 PM - 11:00 PM")
        const regex = /(\d{1,2}):(\d{2})(?:\s*(AM|PM))?\s*-\s*(\d{1,2}):(\d{2})(?:\s*(AM|PM))?/g;
        let match;
        while ((match = regex.exec(occasion.hours)) !== null) {
          let openHour = parseInt(match[1]);
          const openMin = parseInt(match[2]);
          let closeHour = parseInt(match[4]);
          const closeMin = parseInt(match[5]);

          // Handle 12-hour format if AM/PM present
          if (match[3]) {
            if (match[3] === 'PM' && openHour !== 12) openHour += 12;
            if (match[3] === 'AM' && openHour === 12) openHour = 0;
          }
          if (match[6]) {
            if (match[6] === 'PM' && closeHour !== 12) closeHour += 12;
            if (match[6] === 'AM' && closeHour === 12) closeHour = 0;
          }

          const openMinutes = openHour * 60 + openMin;
          const closeMinutes = closeHour * 60 + closeMin;

          if (openMinutes < earliestOpen) earliestOpen = openMinutes;
          if (closeMinutes > latestClose) latestClose = closeMinutes;
        }
      }
    });

    // If we found valid times, format and return the range
    if (earliestOpen < 24 * 60 && latestClose > 0) {
      const openHour = Math.floor(earliestOpen / 60);
      const openMin = earliestOpen % 60;
      const closeHour = Math.floor(latestClose / 60);
      const closeMin = latestClose % 60;

      if (use24HourFormat) {
        return `${String(openHour).padStart(2, '0')}:${String(openMin).padStart(2, '0')} - ${String(closeHour).padStart(2, '0')}:${String(closeMin).padStart(2, '0')}`;
      } else {
        const formatTime = (hour: number, min: number) => {
          const period = hour >= 12 ? 'PM' : 'AM';
          const displayHour = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
          return `${displayHour}:${String(min).padStart(2, '0')} ${period}`;
        };
        return `${formatTime(openHour, openMin)} - ${formatTime(closeHour, closeMin)}`;
      }
    }

    // Fallback to primary occasion (using standardized names)
    const primaryOccasion = enabledOccasions.find(o => o.occasion === 'Carryout/Delivery') ||
                            enabledOccasions.find(o => o.occasion === 'Dine In') ||
                            enabledOccasions.find(o => o.occasion === 'Delivery') ||
                            enabledOccasions[0];

    return primaryOccasion?.hours || 'Hours not available';
  };

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 1, sm: 2 } }}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1 }} />
        ))}
      </Box>
    );
  }

  return (
    <TableContainer 
      component={Paper} 
      elevation={0}
      sx={{ 
        '& .MuiPaper-root': { 
          boxShadow: 'none',
          backgroundColor: 'transparent',
          margin: 0,
          padding: 0
        },
        overflowX: 'auto',
        '& .MuiTable-root': {
          minWidth: { xs: 600, sm: 'auto' }
        }
      }}
    >
      <Table size="small" sx={{ 
        '& .MuiTableCell-root': { 
          px: { xs: 1, sm: 1.5, md: 2 }, 
          whiteSpace: { xs: 'nowrap', sm: 'normal' },
          py: { xs: 0.75, sm: 1 }
        },
        '& .MuiTableHead-root .MuiTableCell-root': {
          py: { xs: 1, sm: 1.25 },
          fontWeight: 600
        }
      }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'background.paper' }}>
            <TableCell padding="checkbox" sx={{ borderBottom: '2px solid', borderColor: 'divider' }}>
              <Checkbox
                checked={isAllSelected}
                indeterminate={isPartiallySelected}
                onChange={() => {
                  if (isAllSelected) {
                    clearSelection();
                  } else {
                    selectAllStores(stores.map(s => s.storeNumber));
                  }
                }}
              />
            </TableCell>
            <TableCell>Store</TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Channel Status</TableCell>
            <TableCell>Today's Hours</TableCell>
            <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Last Modified</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {stores.map((store) => {
            const isSelected = selectedStoreIds.has(store.storeNumber);
            const isExpanded = expandedRows.has(store.storeNumber);

            return (
              <React.Fragment key={store.storeNumber}>
                <TableRow
                  hover
                  selected={isSelected}
                  sx={{
                    '&.MuiTableRow-hover:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isSelected}
                      onChange={() => toggleStoreSelection(store.storeNumber)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>

                  <TableCell sx={{ py: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {getStatusIcon(store)}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          onClick={(e) => {
                            e.stopPropagation();
                            onStoreClick?.(store);
                          }}
                          sx={{
                            fontWeight: 700,
                            cursor: 'pointer',
                            color: 'primary.main',
                            fontSize: '0.875rem',
                            '&:hover': {
                              textDecoration: 'underline',
                            }
                          }}
                        >
                          {store.storeNumber}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ 
                            display: 'block', 
                            whiteSpace: 'nowrap', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            fontSize: '0.75rem'
                          }}
                        >
                          {store.storeName}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {getChannelChips(store).length > 0 ? (
                        getChannelChips(store)
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          No channels configured
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TimeIcon fontSize="small" color="action" />
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {formatTodayHours(store)}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    {store.lastModified ? (
                      <Tooltip
                        title={formatFullTimestamp(store.lastModified.timestamp)}
                        arrow
                        placement="top"
                      >
                        <Box sx={{ cursor: 'default' }}>
                          <Typography variant="body2" color="text.secondary">
                            {formatRelativeTime(store.lastModified.timestamp)}
                          </Typography>
                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                            by {store.lastModified.userName}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No recent changes
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onStoreClick?.(store);
                        }}
                        sx={{ minWidth: 'auto', px: 1 }}
                      >
                        Manage
                      </Button>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleExpand(store.storeNumber);
                        }}
                      >
                        {expandedRows.has(store.storeNumber) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>

                {/* Expanded Row Content */}
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 0, px: 0 }}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: 'background.default' }}>
                        <Stack spacing={3}>
                          {/* Channel Details */}
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              Channel Status Details
                            </Typography>
                            <Stack spacing={1}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                                  Web/App:
                                </Typography>
                                <Chip
                                  label={store.onlineOrders ? 'ENABLED' : 'DISABLED'}
                                  size="small"
                                  color={store.onlineOrders ? 'success' : 'default'}
                                  variant="filled"
                                />
                              </Box>
                              {(store.channels || store.activeChannels)?.map((ch, index) => (
                                <Box key={`${ch.channel}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                                    {ch.channel === 'UBEREATS' ? 'Uber Eats' :
                                     ch.channel === 'DOORDASH' ? 'DoorDash' :
                                     ch.channel === 'GRUBHUB' ? 'Grubhub' :
                                     ch.channel === 'DELIVEROO' ? 'Deliveroo' :
                                     ch.channel === 'JUST_EAT' ? 'Just Eat' : ch.channel}:
                                  </Typography>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                      label={ch.status}
                                      size="small"
                                      color={(ch.status === 'ONLINE' || ch.status === 'ACTIVE') ? 'success' :
                                            ch.status === 'PAUSED' ? 'warning' : 'default'}
                                      variant="filled"
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                      ({ch.deliveryProvider === 'INTERNAL' ? 'In-house delivery' : 'Marketplace delivery'})
                                    </Typography>
                                    {ch.externalStoreId && (
                                      <Typography variant="caption" color="text.secondary">
                                        ID: {ch.externalStoreId}
                                      </Typography>
                                    )}
                                  </Stack>
                                </Box>
                              ))}
                              {(!store.channels || store.channels.length === 0) && (!store.activeChannels || store.activeChannels.length === 0) && (
                                <Typography variant="body2" color="text.secondary">
                                  No third-party delivery channels configured
                                </Typography>
                              )}
                            </Stack>
                          </Box>

                          {/* Store Information */}
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              Store Information
                            </Typography>
                            <Stack spacing={1}>
                              <Box sx={{ display: 'flex', gap: 2 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                                  Address:
                                </Typography>
                                <Typography variant="body2">
                                  {store.address?.street || store.address?.city
                                    ? [store.address?.street, store.address?.city, store.address?.state, store.address?.zip].filter(Boolean).join(', ')
                                    : 'Not available'}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 2 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                                  Timezone:
                                </Typography>
                                <Typography variant="body2">
                                  {store.timezone || 'Not available'}
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>

                          {/* Hours by Occasion */}
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              Weekly Hours by Occasion
                            </Typography>
                            <Stack spacing={2}>
                              {(() => {
                                const occasions = store.hours as any[] || [];
                                const enabledOccasions = occasions.filter(occ => occ.isEnabled);

                                if (enabledOccasions.length === 0) {
                                  return (
                                    <Typography variant="body2" color="text.secondary">
                                      No hours available
                                    </Typography>
                                  );
                                }

                                // Day names for display (ISO order: Mon=1 to Sun=7)
                                const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
                                // Get today's ISO day for highlighting
                                const jsDay = new Date().getDay();
                                const todayIso = jsDay === 0 ? 7 : jsDay;

                                // Helper to format time
                                const formatTimeDisplay = (hour: number, min: number) => {
                                  if (use24HourFormat) {
                                    return `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
                                  } else {
                                    const period = hour >= 12 ? 'PM' : 'AM';
                                    const displayHour = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
                                    return `${displayHour}:${String(min).padStart(2, '0')} ${period}`;
                                  }
                                };

                                return enabledOccasions.map((occasion, idx) => {
                                  const occasionName = formatOccasionName(occasion.occasion);

                                  // Build hours for each day of the week (ISO: 1=Mon to 7=Sun)
                                  const weekHours: { day: string; isoDay: number; hours: string }[] = [];

                                  // Helper to parse duration
                                  const parseDuration = (durationStr: string | number): number => {
                                    if (typeof durationStr === 'string' && durationStr.includes(':')) {
                                      const parts = durationStr.split(':').map(Number);
                                      if (parts.length >= 2) {
                                        return parts[0] * 60 + parts[1];
                                      }
                                    }
                                    return Number(durationStr) || 0;
                                  };

                                  for (let isoDay = 1; isoDay <= 7; isoDay++) {
                                    // Collect ALL hours entries for this day (for split hours)
                                    const dayTimeSlots: { startMinutes: number; display: string }[] = [];

                                    if (occasion.hours) {
                                      for (const hour of occasion.hours) {
                                        if (hour.timeFrame && hour.timeFrame.days && hour.timeFrame.days.includes(isoDay)) {
                                          // Skip special hours (those with date range)
                                          if (hour.timeFrame.startDate && hour.timeFrame.endDate) continue;

                                          const intervalStart = hour.timeFrame.intervalStartTime || '00:00';
                                          const duration = parseDuration(hour.timeFrame.duration || '0');

                                          const [startHour, startMin] = intervalStart.split(':').map(Number);
                                          const startMinutes = startHour * 60 + startMin;
                                          const endMinutes = startMinutes + duration;
                                          const endHour = Math.floor(endMinutes / 60) % 24;
                                          const endMin = endMinutes % 60;

                                          dayTimeSlots.push({
                                            startMinutes,
                                            display: `${formatTimeDisplay(startHour, startMin)} - ${formatTimeDisplay(endHour, endMin)}`
                                          });
                                        }
                                      }
                                    }

                                    let hoursDisplay = 'Closed';
                                    if (dayTimeSlots.length > 0) {
                                      // Sort by start time
                                      dayTimeSlots.sort((a, b) => a.startMinutes - b.startMinutes);
                                      hoursDisplay = dayTimeSlots.map(s => s.display).join('\n');
                                    }

                                    weekHours.push({
                                      day: dayNames[isoDay - 1],
                                      isoDay,
                                      hours: hoursDisplay
                                    });
                                  }

                                  return (
                                    <Box key={idx}>
                                      <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: 'text.primary' }}>
                                        {occasionName}
                                      </Typography>
                                      <Box sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(7, 1fr)',
                                        gap: 0.5,
                                        bgcolor: 'grey.50',
                                        borderRadius: 1,
                                        p: 1
                                      }}>
                                        {weekHours.map(({ day, isoDay, hours }) => {
                                          const isToday = isoDay === todayIso;
                                          return (
                                            <Box
                                              key={day}
                                              sx={{
                                                textAlign: 'center',
                                                p: 0.5,
                                                borderRadius: 1,
                                                bgcolor: isToday ? 'primary.main' : 'transparent',
                                                color: isToday ? 'white' : 'inherit'
                                              }}
                                            >
                                              <Typography
                                                variant="caption"
                                                fontWeight={isToday ? 700 : 600}
                                                sx={{ display: 'block', color: isToday ? 'inherit' : 'text.secondary' }}
                                              >
                                                {day}
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  display: 'block',
                                                  fontSize: '0.65rem',
                                                  whiteSpace: 'pre-line',
                                                  color: hours === 'Closed'
                                                    ? (isToday ? 'rgba(255,255,255,0.7)' : 'text.disabled')
                                                    : 'inherit'
                                                }}
                                              >
                                                {hours}
                                              </Typography>
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                    </Box>
                                  );
                                });
                              })()}
                            </Stack>
                          </Box>

                          {/* Special Hours */}
                          {(() => {
                            // Extract special hours from store data
                            // Special hours have startDate && endDate (regular hours have no date bounds)
                            // Note: With simplified format, regular hours no longer have date segmentation
                            const occasions = store.hours as any[] || [];
                            const specialHoursEntries: {
                              occasion: string;
                              name: string;
                              startDate: Date;
                              endDate: Date;
                              isClosed: boolean;
                              openTime?: string;
                              closeTime?: string;
                            }[] = [];

                            // Helper to format time
                            const formatTimeDisplay = (hour: number, min: number) => {
                              if (use24HourFormat) {
                                return `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
                              } else {
                                const period = hour >= 12 ? 'PM' : 'AM';
                                const displayHour = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
                                return `${displayHour}:${String(min).padStart(2, '0')} ${period}`;
                              }
                            };

                            occasions.forEach((occasion: any) => {
                              if (!occasion.hours) return;

                              occasion.hours.forEach((hour: any) => {
                                const timeFrame = hour.timeFrame || hour;
                                const hasDateRange = timeFrame.startDate && timeFrame.endDate;
                                const isSpecialHours = hasDateRange;

                                if (isSpecialHours) {
                                  // Append T00:00:00 to treat as local date, not UTC (prevents off-by-one day bug)
                                  const startDate = new Date(timeFrame.startDate + 'T00:00:00');
                                  const endDate = new Date(timeFrame.endDate + 'T00:00:00');
                                  const isClosed = timeFrame.blackout === true;

                                  // Parse open/close times
                                  const intervalStart = timeFrame.intervalStartTime || '00:00';
                                  const durationStr = timeFrame.duration || '0';

                                  let duration = 0;
                                  if (typeof durationStr === 'string' && durationStr.includes(':')) {
                                    const parts = durationStr.split(':').map(Number);
                                    if (parts.length >= 2) {
                                      duration = parts[0] * 60 + parts[1];
                                    }
                                  } else {
                                    duration = Number(durationStr) || 0;
                                  }

                                  const [startHour, startMin] = intervalStart.split(':').map(Number);
                                  const endMinutes = (startHour * 60 + startMin) + duration;
                                  const closeHour = Math.floor(endMinutes / 60) % 24;
                                  const closeMin = endMinutes % 60;

                                  const openStr = formatTimeDisplay(startHour, startMin);
                                  const closeStr = formatTimeDisplay(closeHour, closeMin);

                                  // Check for duplicates
                                  const exists = specialHoursEntries.some(e =>
                                    e.startDate.getTime() === startDate.getTime() &&
                                    e.endDate.getTime() === endDate.getTime() &&
                                    e.occasion === occasion.occasion
                                  );

                                  if (!exists) {
                                    specialHoursEntries.push({
                                      occasion: occasion.occasion,
                                      name: timeFrame.name || `Special Hours`,
                                      startDate,
                                      endDate,
                                      isClosed,
                                      openTime: isClosed ? undefined : openStr,
                                      closeTime: isClosed ? undefined : closeStr
                                    });
                                  }
                                }
                              });
                            });

                            // Sort by start date
                            specialHoursEntries.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

                            if (specialHoursEntries.length === 0) {
                              return null;
                            }

                            return (
                              <Box>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <EventIcon fontSize="small" />
                                  Special Hours
                                </Typography>
                                <Stack spacing={1}>
                                  {specialHoursEntries.map((entry, idx) => {
                                    // Compare dates only (not time) - endDate is inclusive (end of day)
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    const endDateOnly = new Date(entry.endDate);
                                    endDateOnly.setHours(23, 59, 59, 999);
                                    const startDateOnly = new Date(entry.startDate);
                                    startDateOnly.setHours(0, 0, 0, 0);
                                    const isPast = endDateOnly < today;
                                    const isActive = startDateOnly <= today && endDateOnly >= today;
                                    const isSameDay = entry.startDate.toDateString() === entry.endDate.toDateString();

                                    return (
                                      <Box
                                        key={idx}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          p: 1,
                                          borderRadius: 1,
                                          bgcolor: isActive ? 'primary.50' : isPast ? 'grey.100' : 'grey.50',
                                          opacity: isPast ? 0.6 : 1,
                                          border: isActive ? '1px solid' : 'none',
                                          borderColor: 'primary.main'
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                          <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <Typography variant="body2" fontWeight={600}>
                                                {isSameDay
                                                  ? format(entry.startDate, 'd MMM yyyy')
                                                  : `${format(entry.startDate, 'd MMM')} — ${format(entry.endDate, 'd MMM yyyy')}`
                                                }
                                              </Typography>
                                              <Chip
                                                label={formatOccasionName(entry.occasion)}
                                                size="small"
                                                sx={{ height: 18, fontSize: '0.65rem' }}
                                              />
                                              {isActive && (
                                                <Chip
                                                  label="ACTIVE"
                                                  size="small"
                                                  color="primary"
                                                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }}
                                                />
                                              )}
                                            </Box>
                                          </Box>
                                        </Box>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 500,
                                            color: entry.isClosed ? 'error.main' : 'text.primary'
                                          }}
                                        >
                                          {entry.isClosed ? 'Closed' : `${entry.openTime} - ${entry.closeTime}`}
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Stack>
                              </Box>
                            );
                          })()}

                          {/* Item Management Info */}
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              Item Availability
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              To view and manage snoozed items for this store, click "Manage" or "View Full Details".
                            </Typography>
                          </Box>

                          {/* Recent Changes */}
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              Recent Activity
                            </Typography>
                            {store.lastModified ? (
                              <Tooltip
                                title={formatFullTimestamp(store.lastModified.timestamp)}
                                arrow
                                placement="top"
                              >
                                <Box sx={{ cursor: 'default', display: 'inline-block' }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {formatRelativeTime(store.lastModified.timestamp)}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    by {store.lastModified.userName}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No recent changes
                              </Typography>
                            )}
                          </Box>

                          {/* Quick Actions */}
                          <Box>
                            <Stack direction="row" spacing={1}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<ViewIcon />}
                                onClick={() => onStoreClick?.(store)}
                              >
                                View Full Details
                              </Button>
                            </Stack>
                          </Box>
                        </Stack>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>

    </TableContainer>
  );
};